import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Descriptions, Select, Space, Table, Typography } from "antd";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";
import { useWarehouses } from "@/lib/useWarehouses";

type Connection = Schemas["ChannelConnection"];
type ArrangeAt = Connection["arrangeAt"];

/**
 * Detail toko > Pengiriman (Plan §6.11): how and when the store's courier is
 * booked, and which Zerpid warehouse each of its marketplace warehouses
 * ships from.
 */
export function StoreFulfillment({ connection, manage }: { connection: Connection; manage: boolean }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const warehouses = useWarehouses();
  const connectionID = connection.id;
  const seen = useQuery({
    queryKey: ["channels", "warehouses", connectionID],
    queryFn: () => unwrap(api.GET("/v1/channels/connections/{connectionID}/warehouses", { params: { path: { connectionID } } })),
  });
  const shipping = useMutation({
    mutationFn: (body: { shipMethod: Connection["shipMethod"]; arrangeAt: ArrangeAt }) =>
      unwrap(api.PUT("/v1/channels/connections/{connectionID}/shipping", { params: { path: { connectionID } }, body })),
    onSuccess: () => {
      void message.success(t("channels.shippingSaved"));
      void queryClient.invalidateQueries({ queryKey: ["channels"] });
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const mapWarehouse = useMutation({
    mutationFn: ({ ext, warehouseId }: { ext: string; warehouseId: string | null }) =>
      unwrap(
        api.PUT("/v1/channels/connections/{connectionID}/warehouses/{externalWarehouseID}", {
          params: { path: { connectionID, externalWarehouseID: ext } },
          body: { warehouseId },
        }),
      ),
    onSuccess: () => {
      void message.success(t("channels.warehouseSaved"));
      void seen.refetch();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const rows = seen.data?.items ?? [];
  return (
    <Space orientation="vertical" size={16} style={{ width: "100%" }}>
      <Descriptions column={1} bordered size="small" title={t("channels.shipping")}>
        <Descriptions.Item label={t("channels.shipMethod")}>
          <Select<Connection["shipMethod"]>
            value={connection.shipMethod}
            disabled={!manage}
            loading={shipping.isPending}
            onChange={(v) => shipping.mutate({ shipMethod: v, arrangeAt: connection.arrangeAt })}
            style={{ minWidth: 240 }}
            options={[
              { value: "pickup", label: t("orders.method.pickup") },
              { value: "dropoff", label: t("orders.method.dropoff") },
            ]}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t("channels.arrangeAt")}>
          <Space orientation="vertical" size={4}>
            <Select<ArrangeAt>
              value={connection.arrangeAt}
              disabled={!manage}
              loading={shipping.isPending}
              onChange={(v) => shipping.mutate({ shipMethod: connection.shipMethod, arrangeAt: v })}
              style={{ minWidth: 240 }}
              options={(["process", "pack", "manual"] as const).map((v) => ({ value: v, label: t(`channels.arrangeAt.${v}`) }))}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t(`channels.arrangeAtHelp.${connection.arrangeAt}`)}
            </Typography.Text>
          </Space>
        </Descriptions.Item>
      </Descriptions>
      <div>
        <Typography.Title level={3} style={{ fontSize: 16 }}>
          {t("channels.marketplaceWarehouses")}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          {t("channels.marketplaceWarehousesHelp")}
        </Typography.Paragraph>
        <Table
          size="small"
          rowKey="externalWarehouseId"
          pagination={false}
          loading={seen.isPending}
          dataSource={rows}
          locale={{ emptyText: t("channels.marketplaceWarehousesEmpty") }}
          columns={[
            { title: t("channels.marketplaceWarehouse"), dataIndex: "externalWarehouseId", render: (v: string) => <span className="z-num">{v}</span> },
            { title: t("channels.orders"), dataIndex: "orders", width: 90, align: "right" },
            {
              title: t("channels.shipsFrom"),
              width: 260,
              render: (_, r) => (
                <Select<string>
                  value={r.warehouseId ?? ""}
                  disabled={!manage}
                  onChange={(v) => mapWarehouse.mutate({ ext: r.externalWarehouseId, warehouseId: v || null })}
                  style={{ width: "100%" }}
                  options={[{ value: "", label: t("channels.warehouseOfStore") }, ...warehouses.active.map((w) => ({ value: w.id, label: w.name }))]}
                />
              ),
            },
          ]}
        />
      </div>
    </Space>
  );
}
